/**
 * InfoPage - Public business info page (Stripe verification)
 * No authentication required. Zero app dependencies.
 * To remove: delete this file + remove import/route from App.tsx
 */

const gallery = [
  { src: '/info-images/forest-morning.jpg', title: 'Forest Morning', subtitle: 'Canvas Painting' },
  { src: '/info-images/mountain-lake.jpg', title: 'Mountain Lake', subtitle: 'Framed Print' },
  { src: '/info-images/golden-meadow.jpg', title: 'Golden Meadow', subtitle: 'Wall Art' },
  { src: '/info-images/waterfall-valley.jpg', title: 'Waterfall Valley', subtitle: 'Statement Piece' },
]

export function InfoPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 bg-white/90 backdrop-blur-sm z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="text-xl font-semibold text-gray-900 tracking-tight">MiniCaryn</span>
          <a
            href="/login"
            className="text-sm font-medium text-[#3D8B47] hover:text-[#327A3C] transition-colors"
          >
            Sign In
          </a>
        </div>
      </header>

      {/* Hero - Mountain Lake landscape photo */}
      <section className="relative w-full overflow-hidden bg-black">
        <img
          src="/info-images/mountain-lake.jpg"
          alt="Mountain lake with crystal clear water reflecting snow-capped peaks"
          className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] object-cover opacity-95"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 md:p-16">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
              Original Nature Art & Paintings
            </h1>
            <p className="text-base sm:text-lg text-gray-200 max-w-2xl leading-relaxed">
              Curated nature-inspired artwork capturing the beauty of landscapes, forests,
              and the natural world through various artistic styles and mediums.
            </p>
          </div>
        </div>
      </section>

      {/* Intro Text */}
      <section className="max-w-6xl mx-auto px-6 py-12 sm:py-16 text-center">
        <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
          MiniCaryn is a curated collection of original nature paintings and landscape-inspired artwork.
          Browse our collection and find the perfect piece to bring the beauty of the natural world into your space.
        </p>
      </section>

      {/* Gallery Grid */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-semibold text-gray-900 mb-8 text-center">Our Collection</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {gallery.map(({ src, title, subtitle }) => (
            <div key={title} className="group overflow-hidden rounded-2xl bg-gray-50">
              <div className="overflow-hidden">
                <img
                  src={src}
                  alt={title}
                  className="w-full h-72 sm:h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">About MiniCaryn</h2>
          <p className="text-gray-600 leading-relaxed">
            MiniCaryn offers original artwork and paintings inspired by nature. Our pieces range from
            watercolor and acrylic paintings to mixed-media landscape art. Each work is carefully crafted
            and available for purchase through the MiniCaryn platform. Whether you're a collector or
            simply love the natural world, you'll find something that speaks to you.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-900 font-medium mb-1">MiniCaryn</p>
          <p className="text-sm text-gray-500">
            <a href="mailto:mincaryn@fansupportdesk.com" className="hover:text-[#3D8B47] transition-colors">
              mincaryn@fansupportdesk.com
            </a>
          </p>
          <p className="text-xs text-gray-400 mt-3">
            &copy; {new Date().getFullYear()} MiniCaryn. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
